import { useRef, useState } from "react";
import { Icon } from "./icons";

const PREROLL = 0.35; // back up slightly so the fragment doesn't clip its first word
const POINT_WINDOW = 8; // seconds to audition for a "point" flag with no real region

/**
 * A tiny play/pause control that auditions one pickup's fragment — plays its
 * chapter audio from `start − preroll` and stops at `end`. Its own `<audio>`
 * (preload="none") so dozens on a page cost nothing until played.
 */
export function PickupPlayer({ src, start, end }: { src: string | null; start: number | null; end: number | null }) {
  const ref = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  if (!src) return null;

  // Where playback stops. A "point" flag (e.g. a manual flag, created with
  // start == end) has no region, so bounding at `end` would stop almost
  // instantly — fall back to a short window from the start so you actually hear
  // the spot. Flags with a real region stop at their end as before.
  const stopAt =
    start != null && (end == null || end <= start + 0.1) ? start + POINT_WINDOW : end;

  function toggle() {
    const a = ref.current;
    if (!a) return;
    if (!a.paused) { a.pause(); return; }
    a.currentTime = Math.max(0, (start ?? 0) - PREROLL);
    a.play().catch(() => {});
  }
  function onTime(e: React.SyntheticEvent<HTMLAudioElement>) {
    const a = e.currentTarget;
    if (stopAt != null && a.currentTime >= stopAt) a.pause();
  }

  return (
    <span className="pickup-player">
      <button className="pkc-play" onClick={toggle} title="Play this fragment">
        <span className="pkc-pi"><Icon name={playing ? "pause" : "play"} size={12} /></span>
        {playing ? "Pause" : "Play audio"}
      </button>
      <audio
        ref={ref}
        src={src}
        preload="none"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        onTimeUpdate={onTime}
      />
    </span>
  );
}
